import React, { useState, useCallback } from 'react';
import {
  Pagination,
  Heading,
  Spinner,
  Link,
  Alert,
  Empty,
  scrollTo,
} from '@printdeal/ui-library';
import { Link as GatsbyLink } from 'gatsby';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from '@gatsbyjs/reach-router';

import ComplaintList from './ComplaintList';
import { messages } from '../../../translations/complaint';
import Breadcrumb from '../../../../Breadcrumb';
import Head from '../../../../PrivateHead';
import { useComplaints } from '../../hooks/useComplaints';
import { Location } from '../../../../../types/Location';

const COMPLAINTS_PER_PAGE = 10;

const ComplaintsPage = () => {
  const intl = useIntl();
  const location = useLocation() as Location;
  const { state } = location;
  const feedback = state?.feedback ? state.feedback : null;
  const [activePage, setActivePage] = useState(1);
  const {
    complaints, total, status, isRefetching,
  } = useComplaints({
    complaintsPerPage: COMPLAINTS_PER_PAGE,
    pageIndex: activePage,
  });

  const handlePaginate = useCallback((pageNumber: number) => {
    setActivePage(pageNumber);
    scrollTo(document.documentElement, {
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, [setActivePage]);

  return (
    <div className="data-sentry-block">
      <Head title={intl.formatMessage(messages.complaintTitle)} />
      <Breadcrumb className="pd-mb-4">
        <Breadcrumb.Item to="..">
          <FormattedMessage id="account.index.title" defaultMessage="My account" />
        </Breadcrumb.Item>
        <Breadcrumb.Item isCurrentPage>
          <FormattedMessage id="complaints.title" defaultMessage="My Complaints" />
        </Breadcrumb.Item>
      </Breadcrumb>

      <div className="pd-mb-5 pd-flex pd-flex-col sm:pd-flex-row sm:pd-items-center sm:pd-text-left">
        <Heading size="1" className="pd-flex pd-grow pd-items-center pd-gap-2">
          <FormattedMessage id="complaints.title" defaultMessage="My Complaints" />
          {isRefetching && (
          <Spinner variant="dim" screenReaderLabel="Loading..." />
          )}
        </Heading>
        <Link
          as={GatsbyLink}
          to="add"
          iconBefore="plus"
          variant="secondary"
          data-cy="linkAddComplaint"
        >
          <FormattedMessage id="complaints.page.addcomplaint" defaultMessage="Add complaint" />
        </Link>
      </div>

      {feedback && (
      <Alert className="pd-mb-5" variant="success" data-cy="alertComplaintAdded" data-hj-suppress>
        {feedback.message}
      </Alert>
      )}
      {status === 'pending' && <Spinner variant="dim" className="pd-my-4" />}
      {status === 'error' && (
      <Alert className="pd-mb-5" variant="error">
        <FormattedMessage
          id="complaints.page.error.loading"
          defaultMessage="Something went wrong while fetching the complaints."
        />
      </Alert>
      )}

      {status === 'success' && complaints.length === 0 && (
      <Empty
        icon="search"
        title={intl.formatMessage(messages.noComplaints)}
        description=""
        className="pd-mb-4"
      />
      )}

      {complaints.length > 0 && (
      <>
        <ComplaintList complaints={complaints} className="pd-mb-5" />
        <Pagination total={total} currentPage={activePage} aria-label={intl.formatMessage({ id: 'general.pagination.label', defaultMessage: 'Pagination' })} perPage={COMPLAINTS_PER_PAGE} siblingPages={3} className="pd-mt-12 pd-justify-center">
          {({ pages, totalPages }) => (
            <>
              <Pagination.Button
                as="button"
                variant="prev"
                onClick={handlePaginate}
              >
                <FormattedMessage id="general.pagination.button.previous" defaultMessage="Previous" />
              </Pagination.Button>

              <Pagination.Items className="pd-hidden sm:pd-flex">
                {pages.map((pageNumber) => (
                  <Pagination.Item
                    as="button"
                    key={pageNumber}
                    onClick={handlePaginate}
                  />
                ))}
              </Pagination.Items>

              <Pagination.Label className="sm:pd-hidden">
                <FormattedMessage
                  id="general.pagination.mobileText"
                  defaultMessage="Page {pageNumber} of {totalPages}"
                  values={{
                    pageNumber: activePage,
                    totalPages,
                  }}
                />
              </Pagination.Label>

              <Pagination.Button
                as="button"
                variant="next"
                onClick={handlePaginate}
              >
                <FormattedMessage id="general.pagination.button.Next" defaultMessage="Next" />
              </Pagination.Button>
            </>
          )}
        </Pagination>
      </>
      )}

      <Link
        as={GatsbyLink}
        to="../"
        iconBefore="chevron-left"
        variant="secondary"
      >
        <FormattedMessage id="account.linkToAccount" defaultMessage="Link to account" />
      </Link>
    </div>
  );
};

export default ComplaintsPage;
